import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";
import { Check, Plus, Loader2, Users } from "lucide-react";

/**
 * ResourceAssignChips — v1.0.1 patch (Item 12 + 15).
 *
 * Per-row chip picker that lets a tenant assign a document to one, many, or
 * "all assistants" (which we model as the company-global flag). Backend:
 *   GET    /api/assistants                           → all assistants of tenant
 *   POST   /api/assistants/{id}/resources            → scope to one assistant
 *   POST   /api/assistants/resources/global          → mark global (all)
 *   DELETE /api/assistants/{id}/resources/{docId}    → unscope
 *   DELETE /api/assistants/resources/global/{docId}  → unmark global
 *
 * Synchronization rule (Item 15): when a resource is mapped to ANY assistant,
 * its mapping rows are scoped to that assistant. When toggled to global, the
 * helper endpoint deletes the scoped rows first. When unmapped from
 * everything, no rows remain. Inactive assistants stay in the dropdown but
 * are visually muted/red.
 */
export default function ResourceAssignChips({ doc, onChange, initialMapping, initialAssistants }) {
  const [open, setOpen] = useState(false);
  // v1.0.10 — seed state from the parent-fetched mapping so the chip summary
  // reflects the PERSISTED assignment immediately on first render, BEFORE the
  // user opens the popover. Previously the chip always showed "+ Assign" on
  // page load even when a backend mapping existed, which read as "assignment
  // was lost" to users.
  const [assistants, setAssistants] = useState(initialAssistants || []);
  const [mappings, setMappings] = useState(() => ({
    scoped: new Set(initialMapping?.scoped ? Array.from(initialMapping.scoped) : []),
    global: Boolean(initialMapping?.global),
  }));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep local state synced when the parent reloads the mapping graph
  // (e.g. after a toggle or after a `rudras:resources-changed` event).
  useEffect(() => {
    setAssistants(initialAssistants || []);
  }, [initialAssistants]);
  useEffect(() => {
    setMappings({
      scoped: new Set(initialMapping?.scoped ? Array.from(initialMapping.scoped) : []),
      global: Boolean(initialMapping?.global),
    });
  }, [initialMapping]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch assistants + global mappings list. The per-assistant resource
      // list is too chatty for every row, so we infer scoping via a single
      // mapping fetch per resource in the future — for v1.0.1 we read from
      // `/api/assistants/{id}/resources` only when the popover opens.
      const { data } = await api.get("/assistants", { params: { include_archived: false } });
      const all = data?.assistants || [];
      setAssistants(all);
      // Determine current mappings for this doc.
      const globalResp = await api.get("/assistants/resources/global");
      const isGlobal = (globalResp.data?.resources || []).some(
        (m) => m.resource_id === doc.id,
      );
      const scopedSet = new Set();
      if (!isGlobal) {
        // Probe each assistant for this resource. Capped at the # of active
        // assistants, which for v1.0.1 is typically 1 (the primary).
        await Promise.all(all.map(async (a) => {
          const r = await api.get(`/assistants/${a.id}/resources`);
          const has = (r.data?.resources || []).some(
            (m) => m.resource_id === doc.id && m.assistant_id === a.id,
          );
          if (has) scopedSet.add(a.id);
        }));
      }
      setMappings({ scoped: scopedSet, global: isGlobal });
    } catch (err) {
      // v1.0.10 — console-only diagnostic for failures (per stakeholder spec).
      // eslint-disable-next-line no-console
      console.warn("[resource-assign] popover refresh failed", err?.response?.status, err?.message);
      // Silent fail; popover will still open. Map state will simply be empty.
    } finally {
      setLoading(false);
    }
  }, [doc.id]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // For the closed-state chip summary we show the count without opening the
  // popover. Read from props or last-known state. (We don't pre-fetch on mount
  // to avoid an N+1 storm across the resources table.)
  const summary = (() => {
    if (mappings.global) return { label: "All assistants", color: "emerald" };
    if (mappings.scoped.size === 0) return null;
    if (mappings.scoped.size === 1) {
      const id = Array.from(mappings.scoped)[0];
      const a = assistants.find((x) => x.id === id);
      return { label: a ? a.assistant_name : "1 assistant", color: "emerald" };
    }
    return { label: `${mappings.scoped.size} assistants`, color: "emerald" };
  })();

  async function toggleAssistant(a) {
    setSaving(true);
    try {
      const isAssigned = mappings.scoped.has(a.id);
      if (isAssigned) {
        await api.delete(`/assistants/${a.id}/resources/${doc.id}`);
      } else {
        // If currently global, unmark global first so the row reflects scoped mappings.
        if (mappings.global) {
          await api.delete(`/assistants/resources/global/${doc.id}`);
        }
        await api.post(`/assistants/${a.id}/resources`, {
          resource_id: doc.id,
          resource_type: "document",
        });
      }
      await refresh();
      onChange?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[resource-assign] toggle assistant failed", a.id, err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.detail || "Could not update assignment");
    } finally {
      setSaving(false);
    }
  }

  async function toggleGlobal() {
    setSaving(true);
    try {
      if (mappings.global) {
        await api.delete(`/assistants/resources/global/${doc.id}`);
      } else {
        await api.post("/assistants/resources/global", {
          resource_id: doc.id,
          resource_type: "document",
        });
      }
      await refresh();
      onChange?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[resource-assign] toggle global failed", err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.detail || "Could not update assignment");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-[11px] font-medium gap-1.5"
          data-testid={`assign-trigger-${doc.id}`}
          title="Assign to assistants"
        >
          {summary ? (
            <Badge
              variant="outline"
              className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider gap-1 font-medium"
            >
              <Users className="w-3 h-3" />
              {summary.label}
            </Badge>
          ) : (
            <>
              <Plus className="w-3 h-3" />
              Assign
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end" data-testid={`assign-popover-${doc.id}`}>
        <div className="px-3 py-2.5 border-b">
          <div className="font-medium text-sm">Assign resource</div>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {doc.title || doc.filename}
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading current mappings…
          </div>
        ) : (
          <div className="max-h-[260px] overflow-y-auto py-1">
            {/* All-assistants global toggle */}
            <button
              type="button"
              onClick={toggleGlobal}
              disabled={saving}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-emerald-50/60 ${
                mappings.global ? "bg-emerald-50/40" : ""
              }`}
              data-testid={`assign-global-${doc.id}`}
            >
              <div
                className={`w-4 h-4 rounded border grid place-items-center ${
                  mappings.global ? "bg-emerald-600 border-emerald-600" : "border-slate-300"
                }`}
              >
                {mappings.global && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="flex-1 text-left">
                <span className="font-medium">All assistants</span>
                <span className="block text-[10px] text-muted-foreground">
                  Shared / company-wide knowledge
                </span>
              </span>
            </button>
            <div className="h-px bg-border my-1" />
            {assistants.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-muted-foreground">
                No assistants available.
              </div>
            ) : (
              assistants.map((a) => {
                const isInactive = (a.lifecycle_state || "").toLowerCase() !== "active";
                const isChecked = mappings.scoped.has(a.id);
                const isDisabled = saving || mappings.global; // global mode overrides scoped picks
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => !isDisabled && toggleAssistant(a)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs ${
                      isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-emerald-50/60"
                    }`}
                    data-testid={`assign-assistant-${a.id}`}
                  >
                    <div
                      className={`w-4 h-4 rounded border grid place-items-center ${
                        isChecked && !mappings.global ? "bg-emerald-600 border-emerald-600" : "border-slate-300"
                      }`}
                    >
                      {isChecked && !mappings.global && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="flex-1 text-left">
                      <span className={`font-medium ${isInactive ? "text-rose-600" : ""}`}>
                        {a.assistant_name}
                        {a.is_primary && (
                          <span className="ml-1 text-[9px] uppercase tracking-wider text-emerald-700">
                            primary
                          </span>
                        )}
                      </span>
                      {isInactive && (
                        <span className="block text-[10px] text-rose-500">Inactive</span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        )}

        {mappings.global && (
          <div className="px-3 py-2 border-t bg-emerald-50/30 text-[10.5px] text-emerald-800">
            Currently shared with all assistants. Uncheck "All assistants" to scope manually.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
